// src/domains/sales/deals/view/UploadTransferReceiptCard.jsx

import { useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Input,
  Label
} from 'reactstrap'
import SalesDealsApi from 'api/admins/sales-deals-api'

const UploadTransferReceiptCard = ({ deal, fetchDeal }) => {
  const [receiptFile, setReceiptFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [result, setResult] = useState(null)

  // 🔑 refs (uncontrolled inputs)
  const bankRef = useRef(null)
  const bankBranchRef = useRef(null)
  const accountNameRef = useRef(null)
  const accountNumberRef = useRef(null)

  /**
   * ==============================
   * Imperative fill from backend
   * ==============================
   * Safe with withLoader (remount-proof)
   */
  useEffect(() => {
    if (!deal) return

    if (bankRef.current) {
      bankRef.current.value = deal.bank || ""
    }
    if (bankBranchRef.current) {
      bankBranchRef.current.value = deal.bank_branch || ""
    }
    if (accountNameRef.current) {
      accountNameRef.current.value = deal.bank_account_name || ""
    }
    if (accountNumberRef.current) {
      accountNumberRef.current.value = deal.bank_account_number || ""
    }
  }, [deal?.id]) // only when deal changes

  /**
   * ==============================
   * Receipt preview
   * ==============================
   */
  const previewUrl =
    receiptFile
      ? URL.createObjectURL(receiptFile)
      : deal?.payment?.transfer_receipt_url

  const isPdf =
    receiptFile?.type?.includes("pdf") ||
    deal?.payment?.transfer_receipt_url?.toLowerCase().endsWith(".pdf")

  const onPick = (e) => {
    setReceiptFile(e.target.files?.[0] || null)
    setResult(null)
  }

  const submit = async () => {
    if (!receiptFile) return

    setUploading(true)
    setResult(null)

    try {
      const formData = new FormData()
      formData.append("transfer_receipt", receiptFile)

      // ✅ read values from refs at submit time
      formData.append("bank", bankRef.current?.value || "")
      formData.append("bank_branch", bankBranchRef.current?.value || "")
      formData.append("bank_account_name", accountNameRef.current?.value || "")
      formData.append("bank_account_number", accountNumberRef.current?.value || "")

      const res = await SalesDealsApi.uploadTransferReceipt({
        dealId: deal.id,
        formData,
      })

      setResult({
        color: "success",
        message: res?.data?.message || "Receipt uploaded successfully",
      })

      setReceiptFile(null)
      await fetchDeal()
    } catch (err) {
      setResult({
        color: "danger",
        message: err?.response?.data?.message || err.message,
      })
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h5">Upload Transfer Receipt (Sales)</CardTitle>

        {/* ==============================
            Preview Section
        ============================== */}
        {previewUrl && (
          <div className="mb-3">
            <Label>Bukti Transfer</Label>
            {isPdf ? (
              <div>
                <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                  Open PDF Receipt
                </a>
              </div>
            ) : (
              <img
                src={previewUrl}
                alt="Transfer Receipt"
                style={{
                  width: "100%",
                  maxHeight: 400,
                  objectFit: "contain",
                  border: "1px solid #eee",
                }}
              />
            )}
          </div>
        )}

        {/* ==============================
            Upload File
        ============================== */}
        <FormGroup>
          <Label>Receipt File</Label>
          <Input
            type="file"
            accept="image/*,application/pdf"
            onChange={onPick}
          />
        </FormGroup>

        {/* ==============================
            Bank Metadata (uncontrolled)
        ============================== */}
        <FormGroup>
          <Label>Bank Name</Label>
          <Input
            innerRef={bankRef}
            placeholder="e.g. BCA"
          />
        </FormGroup>

        <FormGroup>
          <Label>Account Name</Label>
          <Input
            innerRef={accountNameRef}
            placeholder="e.g. JOHN DOE"
          />
        </FormGroup>

        <FormGroup>
          <Label>Account Number</Label>
          <Input
            innerRef={accountNumberRef}
            placeholder="e.g. 1234567890"
          />
        </FormGroup>

        <FormGroup>
          <Label>Bank Branch</Label>
          <Input
            innerRef={bankBranchRef}
            placeholder="e.g. KCP Sudirman"
          />
        </FormGroup>

        {/* ==============================
            Submit
        ============================== */}
        <Button
          block
          color="primary"
          disabled={!receiptFile || uploading}
          onClick={submit}
        >
          {uploading ? "Uploading..." : "Upload Receipt"}
        </Button>

        {result && (
          <Alert color={result.color} className="mt-3">
            {result.message}
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

export default UploadTransferReceiptCard
